import { useState } from 'react';
import {
  useCommonDispatch,
  useCommonSelector,
} from '@wajeez-web/stores/common';
import { SwitchInput, Button } from '@wajeez-web/components/ui';
import { CommonActions, AlertType } from '@wajeez-web/stores/common';
import {
  ContentEntityActions,
  ContentEntityAudiosActions,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DownloadIcon } from '@heroicons/react/solid';
import {
  HasPermissionWrapperComponent,
  useContentEntityDetailsByIdMultilingual,
  useContentEntityItemDetailsByIdMultilingual,
} from '@wajeez-web/components/helpers';
import {
  Languages,
  getLocalTimeFromUTC,
  languages,
} from '@wajeez-web/core/utils';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { MdOutlineEdit } from 'react-icons/md';

import PublishAllCollectionComponentsModal from './PublishAllCollectionComponentsModal';
import { ExpectedPublishDateModal } from './ExpectedPublishDateModal';
import { useAuthSelector } from '@wajeez-web/stores/auth';

const ContentEntityHeaderComponent = ({
  getContentEntityDetails,
  id,
  type,
  title,
  addedLanguages,
  onLanguageSelect,
}: {
  getContentEntityDetails: () => void;
  id?: string;
  type: string;
  title: string;
  addedLanguages?: Languages[];
  onLanguageSelect?: (language: Languages) => void;
}) => {
  const navigate = useNavigate();

  const dispatch = useCommonDispatch();

  const [selectedLanguageId, setSelectedLanguageId] = useState<
    string | undefined
  >();
  const [multiplePublishModalState, setMultiplePublish] = useState(false);
  const [isExpectedPublishDateModalOpen, setIsExpectedPublishDateModalOpen] =
    useState(false);

  const languageId = useCommonSelector(
    ({ common }) => common.settings.LanguageId
  );

  const permissions = useAuthSelector(({ auth }) => auth.permission);

  const { downloadStatus } = useContentSelector((s) => s.contentEntityaudios);
  const contentEntityTypeDetails = useContentSelector(
    (s) => s.contentEntityType.details
  );
  const multiblePubliLoading = useContentSelector(
    (s) => s.contentEntity.multiblePublishStatus === 'loading'
  );
  const { publishStatus } = useContentSelector((s) => s.contentEntity);

  const { data: contentEntityMultilingualData } =
    useContentEntityDetailsByIdMultilingual();

  const contentEntityProfile = contentEntityMultilingualData?.profiles?.find(
    (p) => p.languageId?.toString() === selectedLanguageId
  );

  const { t } = useTranslation();

  const onPublishSuccess = () => {
    getContentEntityDetails();

    if (id) {
      dispatch(
        ContentEntityActions.getContentCollectionAsync({
          ContenteEntityId: id,
          page: 1,
          pageSize: 1000,
          ContenteEntityType: '',
          languageId: selectedLanguageId,
        })
      );
      dispatch(
        ContentEntityActions.getContentparentsAsync({
          ContenteEntityId: id,
          page: 1,
          pageSize: 1000,
          ContenteEntityType: '',
          languageId: selectedLanguageId,
        })
      );
    }

    const stateText = contentEntityProfile?.published
      ? 'Unpublished'
      : 'Published';

    dispatch(
      CommonActions.setAlert({
        show: true,
        type: AlertType.SUCCESS,
        for: 'page',
        messages: [
          {
            message: t(
              `general.${`Content ${stateText} Successfully`}`,
              `Content ${stateText} Successfully`
            ),
            code: 200,
            field: '',
          },
        ],
      })
    );
  };

  const renderLanguagesPublish = () => {
    return addedLanguages?.map((l) => {
      const language = languages.find((ll) => ll.value === l);

      const contentEntityProfile =
        contentEntityMultilingualData?.profiles?.find(
          (p) => p.languageId === l
        );

      return (
        <div key={l} className="rtl:mr-4 ltr:ml-4 flex spasce-s-2 items-center">
          <SwitchInput
            label={`Publish ${language?.label}`}
            checkColor={'green-500'}
            unCheckColor={'red-500'}
            name="publishStatus"
            value={contentEntityProfile?.published}
            onChange={async () => {
              setSelectedLanguageId(l.toString());

              if (
                contentEntityTypeDetails?.lockType === 2 ||
                contentEntityTypeDetails?.lockType === 3
              ) {
                setMultiplePublish(true);
              } else {
                if (contentEntityProfile?.published) {
                  const res = await dispatch(
                    ContentEntityActions.setPublishAsync({
                      id,
                      published: false,
                      languageId: l,
                    })
                  );
                  if (res.meta.requestStatus == 'fulfilled') {
                    onPublishSuccess();
                  }
                } else {
                  const res = await dispatch(
                    ContentEntityActions.setPublishAsync({
                      id,
                      published: true,
                      languageId: l,
                    })
                  );
                  if (res.meta.requestStatus == 'fulfilled') {
                    onPublishSuccess();
                  }
                }
              }
            }}
            loading={id ? publishStatus[id] === 'loading' : false}
          />
        </div>
      );
    });
  };

  const expectedPublishDate = contentEntityMultilingualData?.profiles?.find(
    (p) => p.languageId === languageId
  )?.expectedPublishDate;

  const hasPermissionToEditExpectedPublishDate = permissions?.includes('27.32');

  return id ? (
    <div>
      {' '}
      {downloadStatus === 'loading' && (
        <div className="linear-activity">
          <div className="indeterminate"></div>
        </div>
      )}
      <div className="w-full xl:py-3 lg:py-2 md:py-1 px-6 shadow-md flex justify-between space-s-2">
        <div
          className={`${
            hasPermissionToEditExpectedPublishDate && 'cursor-pointer'
          } text-[16px] font-medium flex justify-center items-center space-s-1`}
          onClick={() => {
            if (hasPermissionToEditExpectedPublishDate) {
              setIsExpectedPublishDateModalOpen(true);
            }
          }}
        >
          <ReturnTranslatedText
            text={`buttons.${'Expected Publish Date'}`}
            origintext="Expected Publish Date"
          />
          <HasPermissionWrapperComponent permissions={['27.32']}>
            <div>
              <MdOutlineEdit />
            </div>
          </HasPermissionWrapperComponent>
          <div>
            {expectedPublishDate &&
              getLocalTimeFromUTC(expectedPublishDate, 'YYYY-MM-DD')}
          </div>
        </div>
        <div className="flex justify-center items-center space-s-2">
          <HasPermissionWrapperComponent permissions={['27.24']}>
            <div className="w-full flex justify-end items-center">
              {renderLanguagesPublish()}
            </div>
          </HasPermissionWrapperComponent>
          <HasPermissionWrapperComponent permissions={['94.5']}>
            <div className="flex spasce-s-2 items-center">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  dispatch(
                    ContentEntityAudiosActions.downloadAudioAsync({
                      id,
                      name: contentEntityMultilingualData?.originalTitle,
                    })
                  );
                }}
              >
                <DownloadIcon className="w-5" />
              </a>
            </div>
          </HasPermissionWrapperComponent>
        </div>
      </div>
      {multiplePublishModalState && (
        <PublishAllCollectionComponentsModal
          isOpen={multiplePublishModalState}
          onClose={async () => {
            setMultiplePublish(false);
          }}
          loading={multiblePubliLoading}
          contentEntityId={id}
          title={
            !contentEntityProfile?.published
              ? 'Publish Collection Components'
              : 'Unpublish Collection Components'
          }
          onSubmit={async (data) => {
            const res =
              data &&
              (await dispatch(
                ContentEntityActions.multiblePublishAsync({
                  languageId: Number(selectedLanguageId),
                  contentEntityPublishedStateInputs: [
                    {
                      contentEntityId: id,
                      published: !contentEntityProfile?.published,
                    },
                  ].concat([], ...(Object.values(data) as any)),
                })
              ));
            if (res?.payload) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const hasError = await res.payload.find(
                (item: { successful: boolean; message: string }) =>
                  !item.successful
              );

              if (hasError) {
                if (hasError.successful === false) {
                  dispatch(
                    CommonActions.setAlert({
                      show: true,
                      type: AlertType.ERROR,
                      for: 'modal',
                      messages: [
                        {
                          message: hasError.message,
                          code: 400,
                          field: '',
                        },
                      ],
                    })
                  );
                }
              } else {
                setMultiplePublish(false);
                const stateText = contentEntityProfile?.published
                  ? 'Unpublished'
                  : 'Published';

                dispatch(
                  CommonActions.setAlert({
                    show: true,
                    type: AlertType.SUCCESS,
                    for: 'page',
                    messages: [
                      {
                        message: t(
                          `general.${`Content ${stateText} Successfully`}`,
                          `Content ${stateText} Successfully`
                        ),
                        code: 200,
                        field: '',
                      },
                    ],
                  })
                );
                getContentEntityDetails();
              }
            }
          }}
          languageId={selectedLanguageId}
        />
      )}
      {isExpectedPublishDateModalOpen && (
        <ExpectedPublishDateModal
          contentEntityId={id}
          dateProfiles={
            contentEntityMultilingualData?.profiles?.map((p) => {
              return {
                languageId: p.languageId!,
                date: p.expectedPublishDate,
              };
            }) || []
          }
          isOpen={isExpectedPublishDateModalOpen}
          onClose={() => {
            setIsExpectedPublishDateModalOpen(false);
          }}
          onSubmit={() => {
            getContentEntityDetails();
          }}
        />
      )}
    </div>
  ) : null;
};

export default ContentEntityHeaderComponent;
